import {isRichTextDocumentEmpty} from '@afriex/core/utils';

import {Button, Header, SpinningLoader} from '@/components';
import RichTextEditor from '@/components/RichTextEditor';
import {uploadBusinessUpdateImage} from '@/services/businessUpdateMediaUpload';
import {BusinessProductUpdateCategory, BusinessUpdateStatus} from '@/types';

import {CATEGORY_STYLES} from '../categoryStyles';
import PreviewCard from './PreviewCard';
import PublishModal from './PublishModal';
import useProductUpdateCompose from './useProductUpdateCompose';

const ProductUpdateCompose = () => {
  const {
    isEditing,
    isLoading,
    isSubmitting,
    isReadOnly,
    existing,
    title,
    setTitle,
    category,
    setCategory,
    content,
    setContent,
    authorName,
    authorRoleLabel,
    isPublishModalOpen,
    setIsPublishModalOpen,
    saveDraft,
    confirmPublish,
    unpublish,
    goToList,
  } = useProductUpdateCompose();

  if (isLoading) {
    return (
      <main className="mt-10 flex justify-center">
        <SpinningLoader size={8} />
      </main>
    );
  }

  return (
    <main className="mt-10 mb-5 mx-4 md:mx-10">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Back"
            onClick={goToList}
            className="text-gray-400 hover:text-white"
          >
            ‹
          </button>
          <Header
            title={isEditing ? existing?.title || 'Edit update' : 'New update'}
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={goToList} disabled={isSubmitting}>
            Cancel
          </Button>
          {!isReadOnly && (
            <>
              <Button
                variant="outline"
                onClick={() => saveDraft().catch(() => {})}
                isLoading={isSubmitting}
              >
                Save draft
              </Button>
              <Button
                variant="solid"
                colorScheme="cyan"
                onClick={() => setIsPublishModalOpen(true)}
                disabled={
                  isSubmitting || !title || isRichTextDocumentEmpty(content)
                }
              >
                Publish
              </Button>
            </>
          )}
          {isReadOnly &&
            existing?.status === BusinessUpdateStatus.Published && (
              <Button
                variant="outline"
                colorScheme="red"
                onClick={() => unpublish().catch(() => {})}
                isLoading={isSubmitting}
              >
                Unpublish
              </Button>
            )}
        </div>
      </div>

      {isReadOnly && (
        <p className="mt-2 text-sm text-yellow-500">
          This update is {existing?.status.toLowerCase()} and can no longer be
          edited or deleted — only Draft or Scheduled updates can be changed.
        </p>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        <div className="flex flex-col gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-300">
              Title
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              // Matches the server's title limit.
              maxLength={200}
              disabled={isReadOnly}
              className="w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-2 text-gray-100 disabled:opacity-60"
              placeholder="New endpoint: Bulk Payout API (beta)"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-300">
              Category
            </label>
            <div className="flex gap-2">
              {Object.values(BusinessProductUpdateCategory).map((value) => (
                <button
                  key={value}
                  type="button"
                  disabled={isReadOnly}
                  onClick={() => setCategory(value)}
                  className={`rounded-md border px-4 py-2 text-sm font-medium disabled:opacity-60 ${
                    category === value
                      ? 'border-blue-500 bg-blue-600 text-white'
                      : 'border-gray-700 text-gray-300'
                  }`}
                >
                  {CATEGORY_STYLES[value].label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-300">
              Message
            </label>
            <RichTextEditor
              value={content}
              onChange={setContent}
              editable={!isReadOnly}
              onUploadImage={uploadBusinessUpdateImage}
              ariaLabel="Message"
            />
          </div>

          {!isReadOnly && (
            <p className="text-xs text-gray-500">
              Delivered to all Business Portal users in real time · Publishers:
              Ops, Growth, Product, Engineering
            </p>
          )}
        </div>

        <div>
          <p className="mb-2 text-xs uppercase tracking-wide text-gray-500">
            Preview — how it appears in the portal
          </p>
          <PreviewCard
            title={title}
            content={content}
            category={category}
            authorName={authorName}
            authorRoleLabel={authorRoleLabel}
          />
        </div>
      </div>

      <PublishModal
        isOpen={isPublishModalOpen}
        onClose={() => setIsPublishModalOpen(false)}
        title={title}
        category={category}
        authorName={authorName}
        authorRoleLabel={authorRoleLabel}
        isSubmitting={isSubmitting}
        onConfirm={(publishAt) => confirmPublish(publishAt).catch(() => {})}
      />
    </main>
  );
};

export default ProductUpdateCompose;
